import type { Candle, TimeframeKey, UTBotConfig, UTBotSignal, ATRResult } from '../types/index.js';
import { generateId } from './index.js';
import { TIMEFRAME_MS } from '../constants/index.js';

// ─── Core ATR Calculation ─────────────────────────────────────────────────────

/**
 * True Range for a single candle.
 * Requires the previous close; if unavailable (first candle) use high - low.
 */
function trueRange(candle: Candle, prevClose?: number): number {
  const hl  = candle.high - candle.low;
  if (prevClose === undefined) return hl;
  const hpc = Math.abs(candle.high - prevClose);
  const lpc = Math.abs(candle.low  - prevClose);
  return Math.max(hl, hpc, lpc);
}

/**
 * Wilder's smoothed ATR (same method as TradingView's built-in ATR).
 * First ATR = simple average of first `period` TRs.
 * Subsequent = (prev ATR × (period - 1) + current TR) / period.
 */
function computeATR(candles: Candle[], period: number): number[] {
  if (candles.length < period) return [];

  const trs = candles.map((c, i) =>
    i === 0 ? c.high - c.low : trueRange(c, candles[i - 1]!.close)
  );

  const atrs: number[] = new Array(candles.length).fill(0);

  // Seed with SMA of first `period` TRs
  const seed = trs.slice(0, period).reduce((a, b) => a + b, 0) / period;
  atrs[period - 1] = seed;

  // Wilder smoothing
  for (let i = period; i < candles.length; i++) {
    atrs[i] = ((atrs[i - 1]! * (period - 1)) + trs[i]!) / period;
  }

  return atrs;
}

// ─── UT Bot Trail Stop ────────────────────────────────────────────────────────

interface TrailStopResult {
  trailStops: number[];
  signals:    Array<{ index: number; type: 'BUY' | 'SELL'; price: number; atr: number }>;
}

/**
 * UT Bot Algorithm by @QuantNomad (ported from Pine Script).
 *
 * Trail stop calculation:
 *   nLoss = keyValue × ATR(period)
 *   If src > prevTrail → trail = max(prevTrail, src − nLoss)
 *   If src < prevTrail → trail = min(prevTrail, src + nLoss)
 *
 * Signal:
 *   BUY  = prev close was ≤ trail AND current close crossed above trail
 *   SELL = prev close was ≥ trail AND current close crossed below trail
 */
function computeTrailStop(
  candles: Candle[],
  atrs: number[],
  keyValue: number,
  period: number
): TrailStopResult {
  const trailStops: number[] = new Array(candles.length).fill(0);
  const signals: TrailStopResult['signals'] = [];

  let prevTrail = 0;

  for (let i = period; i < candles.length; i++) {
    const src   = candles[i]!.close;
    const nLoss = keyValue * (atrs[i] ?? 0);

    let trail: number;
    if (prevTrail === 0) {
      trail = src - nLoss;
    } else if (src > prevTrail) {
      trail = Math.max(prevTrail, src - nLoss);
    } else {
      trail = Math.min(prevTrail, src + nLoss);
    }

    trailStops[i] = trail;

    // Crossover detection (requires i > period to have a valid prevTrail)
    if (i > period && prevTrail !== 0) {
      const prevSrc = candles[i - 1]!.close;
      const prevStop = trailStops[i - 1]!;

      if (prevSrc <= prevStop && src > trail) {
        signals.push({ index: i, type: 'BUY',  price: src, atr: atrs[i] ?? 0 });
      } else if (prevSrc >= prevStop && src < trail) {
        signals.push({ index: i, type: 'SELL', price: src, atr: atrs[i] ?? 0 });
      }
    }

    prevTrail = trail;
  }

  return { trailStops, signals };
}

// ─── Public API ───────────────────────────────────────────────────────────────

export interface UTBotResult {
  trailStops: number[];   // one value per candle (0 for the warmup period)
  signals:    UTBotSignal[];
  atrs:       ATRResult[];
}

/**
 * True once the candle that opened at `timeMs` has closed. A UT Bot signal on
 * a still-forming candle can vanish before the close, so only signals on
 * closed candles are final (traded, alerted).
 */
export function isClosedCandle(timeMs: number, timeframe: TimeframeKey, nowMs = Date.now()): boolean {
  return timeMs + TIMEFRAME_MS[timeframe] <= nowMs;
}

export const UTBOT_MAX_ATR_PERIOD = 200;

/**
 * KEY VAL / ATR PERIOD as typed into the UI can be blank or out of range
 * (NaN, 0, negative, fractional ATR). Swap any invalid field for its default
 * so the chart and the server's signal scan always run the same numbers.
 */
export function resolveUTBotSettings(
  keyValue: unknown,
  atrPeriod: unknown,
  defaults: { keyValue: number; atrPeriod: number },
): { keyValue: number; atrPeriod: number } {
  const kv  = Number(keyValue);
  const atr = Number(atrPeriod);
  return {
    keyValue:  kv > 0 ? kv : defaults.keyValue,
    atrPeriod: Number.isInteger(atr) && atr >= 1 && atr <= UTBOT_MAX_ATR_PERIOD ? atr : defaults.atrPeriod,
  };
}

/**
 * Run the full UT Bot calculation.
 * Pure function — safe to call in useMemo, a worker loop, or a backtest.
 */
export function runUTBot(
  candles: Candle[],
  config: UTBotConfig,
  symbol: string
): UTBotResult {
  const { keyValue, atrPeriod, timeframe } = config;

  if (candles.length < atrPeriod + 2) {
    return { trailStops: [], signals: [], atrs: [] };
  }

  const atrs      = computeATR(candles, atrPeriod);
  const { trailStops, signals: raw } = computeTrailStop(candles, atrs, keyValue, atrPeriod);

  const atrResults: ATRResult[] = atrs.map((atr, index) => ({
    index,
    atr,
    trailStop: trailStops[index] ?? 0,
  }));

  const utSignals: UTBotSignal[] = raw.map(s => ({
    id:          generateId(),
    symbol,
    type:        s.type,
    price:       s.price,
    trailStop:   trailStops[s.index] ?? 0,
    atr:         s.atr,
    candleIndex: s.index,
    timestamp:   (candles[s.index]?.time ?? 0) * 1000, // to ms
    timeframe,
    config:      { ...config },
  }));

  return { trailStops, signals: utSignals, atrs: atrResults };
}

// ─── Signal Accuracy Helpers ─────────────────────────────────────────────────

export interface BacktestResult {
  totalSignals: number;
  buySignals:   number;
  sellSignals:  number;
  /** Naive accuracy: % of BUYs where next close > signal price */
  buyAccuracy:  number;
  /** Naive accuracy: % of SELLs where next close < signal price */
  sellAccuracy: number;
  avgATR:       number;
}

/**
 * Quick naive backtest — checks if the signal direction was correct
 * by comparing signal price to the close N candles later.
 */
export function backtestUTBot(
  candles: Candle[],
  signals: UTBotSignal[],
  lookForward = 3
): BacktestResult {
  let buyCorrect = 0, sellCorrect = 0;
  let buyCount = 0, sellCount = 0;
  let atrSum = 0;

  for (const sig of signals) {
    const futureIdx = sig.candleIndex + lookForward;
    const futureCandle = candles[futureIdx];
    atrSum += sig.atr;

    if (sig.type === 'BUY') {
      buyCount++;
      if (futureCandle && futureCandle.close > sig.price) buyCorrect++;
    } else {
      sellCount++;
      if (futureCandle && futureCandle.close < sig.price) sellCorrect++;
    }
  }

  return {
    totalSignals: signals.length,
    buySignals:   buyCount,
    sellSignals:  sellCount,
    buyAccuracy:  buyCount  > 0 ? (buyCorrect  / buyCount)  * 100 : 0,
    sellAccuracy: sellCount > 0 ? (sellCorrect / sellCount) * 100 : 0,
    avgATR:       signals.length > 0 ? atrSum / signals.length : 0,
  };
}
